import { useEffect, useMemo } from "react";
import { Column } from "../../../../core/components/Column";
import { Row } from "../../../../core/components/Row";
import { TextComponent } from "../../../../core/components/TextComponent";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Chats } from "../../../chat/domain/entities/getChat";
import { setSelectedChat } from "../../../chat/presentation/store/ChatSlice";
import { Chat } from "../../../../core/components/Chat";
import { onGetNotification } from "../../../notification/presentation/store/NotificationThunk";
import InfoNotificationAdmin from "../../../notification/domain/entities/InfoNotificationAdmin";
import { alertReceived } from "../store/MatchSlice";

export const AsideNotification = ({
	profileImage,
	username,
}: {
	profileImage: string;
	username: string;
}) => {
	const { chats } = useSelector((state: RootState) => state.chat);
	const { myNotifications } = useSelector((state: RootState) => state.match);
	const { notificationAlert } = useSelector((state: RootState) => state.notification);
	const dispatch: AppDispatch = useDispatch();
	const navigate = useNavigate();

	const handleSeeAllMessages = () => {
		navigate("/messages");
	};

	const handleChatClick = (chat: Chats) => {
		dispatch(setSelectedChat(chat));
		navigate(`/messages/${chat.user_id}`);
	};

	useEffect(() => {
		const fetchRecentAlerts = async () => {
			if (myNotifications.alert.length > 0 || notificationAlert.length > 0) return;
			const result = await dispatch(
				onGetNotification({
					params: { page: "1", limit: "4" },
				}),
			);
			const payload = result?.payload as InfoNotificationAdmin[] | undefined;
			if (Array.isArray(payload) && payload.length > 0) {
				dispatch(alertReceived({ data: payload }));
			}
		};
		fetchRecentAlerts();
	}, [dispatch, myNotifications.alert.length, notificationAlert.length]);

	const recentAlerts = useMemo(() => {
		const mergedAlerts = [...myNotifications.alert, ...notificationAlert];
		const uniqueAlertsById = mergedAlerts.filter((alert, index, array) => {
			if (!alert?.id) return false;
			return array.findIndex((item) => item.id === alert.id) === index;
		});

		return uniqueAlertsById
			.sort(
				(a, b) =>
					new Date(b.date_at || 0).getTime() - new Date(a.date_at || 0).getTime(),
			)
			.slice(0, 4);
	}, [myNotifications.alert, notificationAlert]);

	const formatAlertTime = (dateAt?: string) => {
		if (!dateAt) return "";
		const parsed = new Date(dateAt);
		if (Number.isNaN(parsed.getTime())) return "";
		return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
	};

	const truncate = (value?: string, max = 70) => {
		if (!value) return "";
		return value.length > max ? `${value.slice(0, max)}...` : value;
	};

	return (
		<Column
			crossAxisAlignment="start"
			mainAxisAlignment="start"
			className="z-10 max-xl:hidden min-w-[25rem] max-w-[27rem] h-full pl-8 border-l border-l-grayLight gap-5 bg-grayDefault">
			<Row crossAxisAlignment="center" className="gap-3.5">
				<img
					className="w-12 h-12 object-cover rounded-full select-none"
					src={profileImage}
					draggable={false}
					alt={`profile-${username}`}
				/>
				<TextComponent variant="h6" className="font-semibold">
					{username}
				</TextComponent>
			</Row>
			<Column className="bg-white p-4 rounded-lg w-full">
				<Row mainAxisAlignment="between" className="mb-4">
					<TextComponent variant="h6" className="font-semibold">
						Recent Notifications
					</TextComponent>
					{chats.length > 0 && recentAlerts.length === 0 && (
						<button onClick={handleSeeAllMessages} className="select-none">
							<TextComponent
								variant="h6"
								className="font-semibold text-primary">
								See All
							</TextComponent>
						</button>
					)}
				</Row>
				<hr className="border-t border-t-grayLight" />
				{recentAlerts.length > 0 ? (
					<Column className="gap-2 pt-2">
						{recentAlerts.map((alert) => (
							<Column
								key={alert.id}
								className="border border-gray-200 rounded-lg p-3 gap-1"
							>
								<Row mainAxisAlignment="between" crossAxisAlignment="center">
									<TextComponent variant="h6" className="font-semibold">
										{alert.title}
									</TextComponent>
									<TextComponent variant="h7" className="text-grayLight2">
										{formatAlertTime(alert.date_at)}
									</TextComponent>
								</Row>
								<TextComponent variant="h7" className="text-grayLight2">
									{truncate(alert.subtitle)}
								</TextComponent>
							</Column>
						))}
					</Column>
				) : (
					chats.slice(0, 4).map((chat, index) => (
						<div
							key={index}
							onClick={() => chat && handleChatClick(chat)}
						>
							<Chat
								{...(chat as Chats)}
							/>
						</div>
					))
				)}
			</Column>
		</Column>
	);
};
